import React, { useState, useEffect } from "react";
import { message, Button, DatePicker, Select } from "antd";
import { getRequest, postRequest } from "../service/index";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const RunNewPayRoll = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  // Only date range is used
  // Removed filters - showing all employees
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    employeeIds: []
  });

  // Get current month start and end dates
  const getCurrentMonthRange = () => {
    const now = dayjs();
    const startDate = now.startOf('month');
    const endDate = now.endOf('month');
    return { startDate, endDate };
  };

  // No month/year options needed - only date range

  // Fetch employees on component mount
  useEffect(() => {
    const { startDate, endDate } = getCurrentMonthRange();
    fetchEmployees(startDate, endDate);
  }, []);

  // Set default date range when component mounts
  useEffect(() => {
    const { startDate, endDate } = getCurrentMonthRange();
    setFormData(prev => ({
      ...prev,
      startDate: startDate,
      endDate: endDate
    }));
  }, []);

  // No filters needed - showing all employees

  const fetchEmployees = async (startDate, endDate) => {
    setEmployeesLoading(true);
    try {
      let url = "/api/companies/reportto";
      if (startDate && endDate) {
        url += `?startDate=${startDate.format('YYYY-MM-DD')}&endDate=${endDate.format('YYYY-MM-DD')}`;
      }
      
      const response = await getRequest(url);
      if (response?.employees && Array.isArray(response.employees)) {
        setEmployees(response.employees);
        const allEmployeeIds = response.employees.map(emp => emp._id);
        setSelectedEmployees(response.employees);
        setFormData(prev => ({
          ...prev,
          employeeIds: allEmployeeIds
        }));
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      message.error("Failed to fetch employees");
    } finally {
      setEmployeesLoading(false);
    }
  };

  // No filter functions needed

  // This function is now defined below

  // No filter functions needed

  // No filter options needed

  // No filtering needed - all employees are available

  // No filtering needed - all employees are selected by default

  // No month/year handlers needed

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFormData(prev => ({
        ...prev,
        startDate: dates[0],
        endDate: dates[1]
      }));
      // Fetch employees with new date range
      fetchEmployees(dates[0], dates[1]);
    }
  };

  const handleEmployeeSelection = (selectedEmployeeIds) => {
    const selectedEmps = employees.filter(emp => selectedEmployeeIds.includes(emp._id));
    setSelectedEmployees(selectedEmps);
    setFormData(prev => ({
      ...prev,
      employeeIds: selectedEmployeeIds
    }));
  };

  // No toggle needed - only date range

  // No filter handling functions needed

  // No filter functions needed

  // No filter selection handling needed

  const handleSubmit = async () => {
    if (!formData.startDate || !formData.endDate) {
      message.error("Please select start and end dates");
      return;
    }

    if (formData.employeeIds.length === 0) {
      message.error("Please select at least one employee");
      return;
    }

    setLoading(true);
    try {
      // Payload format: {startDate, endDate, employeeIds}
      const payload = {
        startDate: formData.startDate.format('YYYY-MM-DD'),
        endDate: formData.endDate.format('YYYY-MM-DD'),
        employeeIds: formData.employeeIds
      };

      console.log('Sending payload:', payload);
      
      const response = await postRequest("/api/monthlysalery", payload);
      
      if (response?.success) {
        message.success("Payroll run successfully!");
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(response?.message || "Failed to run payroll");
      }
    } catch (error) {
      console.error("Error running payroll:", error);
      message.error("Failed to run payroll. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // No filtering needed

  return (
    <div className="flex flex-col gap-4 max-h-[75vh] overflow-y-auto pr-2">
      {/* Date Range Selection */}
      <div className="flex flex-col gap-2">
        <label className="text-md font-medium">
          Payroll Period
        </label>
        <RangePicker
          value={[formData.startDate, formData.endDate]}
          onChange={handleDateRangeChange}
          className="w-full"
          format="YYYY-MM-DD"
          placeholder={['Start Date', 'End Date']}
        />
      </div>

      {/* Employee Selection */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-md font-medium">
            Select Employees ({selectedEmployees.length} selected)
            {employeesLoading && <span className="ml-2 text-blue-500">Loading...</span>}
          </label>
          <div className="flex gap-2">
            <Button 
              size="small" 
              type="primary"
              className="bg-primary text-white"
              onClick={() => {
                const allIds = employees.map(emp => emp._id);
                setSelectedEmployees(employees);
                setFormData(prev => ({ ...prev, employeeIds: allIds }));
              }}
              disabled={selectedEmployees.length === employees.length}
            >
              Select All
            </Button>
            <Button 
              size="small" 
              onClick={() => {
                setSelectedEmployees([]);
                setFormData(prev => ({ ...prev, employeeIds: [] }));
              }}
              disabled={selectedEmployees.length === 0}
            >
              Clear All
            </Button>
          </div>
        </div>
        <Select
          mode="multiple"
          placeholder={employeesLoading ? "Loading employees..." : "Select employees"}
          value={formData.employeeIds}
          onChange={handleEmployeeSelection}
          options={employees.map(emp => ({
            value: emp._id,
            label: `${emp.name} - ${emp.designation} (${emp.department}) - ${emp.location}`
          }))}
          className="w-full"
          maxTagCount="responsive"
          showSearch
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          loading={employeesLoading}
          disabled={employeesLoading}
        />
      </div>

      {/* Selected Employees Display */}
      {selectedEmployees.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-gray-600">Selected Employees:</span>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {selectedEmployees.map(emp => (
              <div 
                key={emp._id}
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm flex items-center gap-2"
              >
                <span>{emp.name} ({emp.designation})</span>
                <button
                  onClick={() => {
                    const newSelected = selectedEmployees.filter(e => e._id !== emp._id);
                    const newIds = newSelected.map(e => e._id);
                    setSelectedEmployees(newSelected);
                    setFormData(prev => ({
                      ...prev,
                      employeeIds: newIds
                    }));
                  }}
                  className="text-blue-600 hover:text-blue-800 ml-2"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-end gap-3 mt-4">
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          type="primary"
          className="bg-primary text-white"
          disabled={
            !formData.startDate || !formData.endDate || formData.employeeIds.length === 0
          }
        >
          {loading ? "Running Payroll..." : "Run Payroll"}
        </Button>
      </div>
    </div>
  );
};

export default RunNewPayRoll;